import React from "react";
import { getDevelopmentType } from "../investment-card";
import { formatNumber } from "@/utils/formatNumber";

interface InvestmentData {
  id: string;
  investmentType: string;
  proposedLocation: string;
  description: string;
  totalInvestmentAmount: number;
  minInvestmentAmountPerSubscribers: number;
  expectedROI: number;
  developmentStage: string;
  amountAlreadyRaised: number;
  propertyType?: string;
  bedroomCount?: number;
  bathroomCount?: number;
  furnished?: boolean;
  imageUrl: string[];
  landType?: string;
  landPrice?: number;
  landVolume?: number;
  landInitialDepositPrice?: number;
  landSubsequentPayment?: number;
}

interface SubscriptionCardProps {
  investment: InvestmentData;
  progressToMaturity?: number;
}

const SubscriptionCard: React.FC<SubscriptionCardProps> = ({
  investment,
  progressToMaturity = 0,
}) => {
  const isProperty = investment?.investmentType
    .toLowerCase()
    .includes("property");

  const fundingProgress =
    (investment?.amountAlreadyRaised / investment?.totalInvestmentAmount) * 100;
  const backgroundImageUrl =
    investment?.imageUrl?.length > 0
      ? `url(${investment.imageUrl[0]})`
      : "url(/assets/images/property-default.svg)";

  return (
    <div className="relative flex gap-3 bg-base-white rounded-lg shadow-lg p-6 w-[660px] h-[381px]">
      <div className="rounded-tl-lg w-[200px] h-[100%]">
        <div
          className="absolute top-0 left-0 w-[199px] h-[200px] rounded-tl-lg bg-cover bg-center"
          style={{
            backgroundImage: backgroundImageUrl,
          }}
        />
        <div className="w-[199px] absolute top-[200px] left-0 mt-[34px] flex flex-col items-center justify-center gap-4">
          <div className="w-[55px] h-[55px]">
            <svg
              className="w-full h-full transform -rotate-90"
              viewBox="0 0 100 100"
            >
              <circle
                cx="50"
                cy="50"
                r="40"
                fill="transparent"
                stroke="#133345"
                strokeWidth="14"
              />
              <circle
                cx="50"
                cy="50"
                r="40"
                fill="transparent"
                stroke="#2D96A7"
                strokeWidth="14"
                strokeDasharray="251.2"
                strokeDashoffset={251.2 * (1 - fundingProgress / 100)}
              />
            </svg>
          </div>
          <div>
            <p className="text-xs text-grey-7 font-bold">
              {fundingProgress.toFixed(2)}% funded
            </p>
          </div>
        </div>
      </div>

      <div className="flex-1">
        <h2 className="text-lg text-main-900 font-bold mb-4">
          {investment?.description}
        </h2>
        {/* <h2 className="text-lg text-main-900 font-bold mb-4">
          - {investment?.proposedLocation}
        </h2> */}
        <div className="p-2 bg-grey-6">
          <div className="flex flex-col gap-2">
            <div className="flex justify-between">
              <p className="text-gray-600 font-medium">Investment Type</p>
              <p className="text-gray-800 font-bold">
                {getDevelopmentType(investment?.investmentType)}
              </p>
            </div>
            <div className="flex justify-between">
              <p className="text-gray-600 font-medium">Proposed Location</p>
              <p className="text-gray-800 font-bold">
                {investment?.proposedLocation}
              </p>
            </div>
            <div className="flex justify-between">
              <p className="text-gray-600 font-medium">Total Investment</p>
              <p className="text-gray-800 font-bold">
                NGN{" "}
                {formatNumber(
                  investment?.totalInvestmentAmount?.toLocaleString(),
                )}
              </p>
            </div>
            <div className="flex justify-between">
              <p className="text-gray-600 font-medium">Minimum Investment</p>
              <p className="text-gray-800 font-bold">
                NGN{" "}
                {formatNumber(
                  investment?.minInvestmentAmountPerSubscribers?.toLocaleString(),
                )}
              </p>
            </div>
            <div className="flex justify-between">
              <p className="text-gray-600 font-medium">Expected ROI</p>
              <p className="text-gray-800 font-bold">
                {investment?.expectedROI}%
              </p>
            </div>
            <div className="flex justify-between">
              <p className="text-gray-600 font-medium">Development Stage</p>
              <p className="text-gray-800 font-bold">
                {investment?.developmentStage}
              </p>
            </div>
            {isProperty ? (
              <>
                <div className="flex justify-between">
                  <p className="text-gray-600 font-medium">Property Type</p>
                  <p className="text-gray-800 font-bold">
                    {investment?.propertyType}
                  </p>
                </div>
                <div className="flex justify-between">
                  <p className="text-gray-600 font-medium">Specifications</p>
                  <p className="text-gray-800 font-bold">
                    {investment?.bedroomCount}bed, {investment?.bathroomCount}
                    bath
                    {investment?.furnished ? " (Furnished)" : ""}
                  </p>
                </div>
              </>
            ) : (
              <>
                <div className="flex justify-between">
                  <p className="text-gray-600 font-medium">Land Type</p>
                  <p className="text-gray-800 font-bold">
                    {investment?.landType ?? "-"}
                  </p>
                </div>
                <div className="flex justify-between">
                  <p className="text-gray-600 font-medium">Land Volume</p>
                  <p className="text-gray-800 font-bold">
                    {investment?.landVolume ?? "-"} sqm
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionCard;
